import { useBottomSheetScrollableCreator } from '@gorhom/bottom-sheet';
import { Check, Search, SearchX } from '@tamagui/lucide-icons';
import { InputRef } from '@unistocks/components/basic/input';
import { NativePressable } from '@unistocks/components/basic/pressable';
import { Highlighter } from '@unistocks/components/highlighter/highlighter';
import { Input } from '@unistocks/components/input/input';
import { Column } from '@unistocks/components/layout/flex';
import { EmptyList } from '@unistocks/components/no-data/empty-list';
import { NoDataProps } from '@unistocks/components/no-data/no-data';
import { ItemSeparator } from '@unistocks/components/select/item-separator';
import { Sheet, SheetProps } from '@unistocks/components/sheet/sheet';
import { useSheetContainerStyles } from '@unistocks/components/sheet/utils';
import { ListRenderItem, VirtualList } from '@unistocks/components/virtual-list/list';
import {
  CANCELLED_ERROR,
  DEBOUNCE_TIME,
  FONT_FAMILY_SEMI_BOLD,
  SUBTITLE_OPACITY
} from '@unistocks/shared/consts';
import { useBackHandler } from '@unistocks/shared/hooks/use-backhandler';
import { t } from '@unistocks/shared/localization/index.ts';
import { Logger } from '@unistocks/shared/services/logger.service';
import { equals, isDefined, isPromise } from '@unistocks/shared/utils/util';
import { SelectOption } from '@unistocks/types';
import { IconProp } from '@unistocks/types/icon-prop';
import type { MaybePromise } from '@unistocks/types/util';
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type PropsWithChildren,
  type Ref
} from 'react';
import { Keyboard, StyleSheet } from 'react-native';
import { ListItem, Spinner, getFontSize, useControllableState } from 'tamagui';

export type TypeaheadRef = {
  open: () => void;
  close: () => void;
  focus: () => void;
  blur: () => void;
};

/**
 * Remote search provider. Called with the debounced query and expected to return the options to display.
 */
export type TypeaheadSearch<TValue = string> = (
  query: string
) => MaybePromise<ReadonlyArray<SelectOption<TValue>>>;

/**
 * Local filter applied to the provided options.
 */
export type TypeaheadFilter<TValue = string> = (
  query: string,
  options: ReadonlyArray<SelectOption<TValue>>
) => ReadonlyArray<SelectOption<TValue>>;

export type TypeaheadProps<TValue = string> = PropsWithChildren<
  Omit<SheetProps, 'onChange' | 'children' | 'ref'>
> & {
  ref?: Ref<TypeaheadRef>;
  value?: TValue;
  onChange?: (value?: TValue) => void;
  disabled?: boolean;
  /** Placeholder for the trigger. */
  placeholder?: string;
  /** Placeholder for the search field. Defaults to `placeholder`. */
  searchPlaceholder?: string;
  /** Source list. Shown while below `minSearchLength`, and passed to `filter`. */
  options?: ReadonlyArray<SelectOption<TValue>>;
  /** Remote search. Takes precedence over `filter`. */
  search?: TypeaheadSearch<TValue>;
  /** Custom local filter for `options`. Ignored when `search` is provided. */
  filter?: TypeaheadFilter<TValue>;
  /** Debounce in ms for `search`. @default DEBOUNCE_TIME */
  debounceTime?: number;
  /** Minimum query length before searching. @default 0 without `search`, 3 with `search` */
  minSearchLength?: number;
  keyExtractor?: (item: SelectOption<TValue>, index: number) => string;
  renderItemIcon?: (item: SelectOption<TValue>) => IconProp;
  valueFormatter?: (value: TValue) => string;
  noDataText?: NoDataProps['text'];
  noDataIcon?: NoDataProps['Icon'];
  noDataColor?: NoDataProps['color'];
  noDataSize?: NoDataProps['size'];
};

const EMPTY: ReadonlyArray<never> = [];

const defaultKeyExtractor = (item: SelectOption, index: number) => item.key || index.toString();

const defaultFilter = <T,>(
  query: string,
  options: ReadonlyArray<SelectOption<T>>
): ReadonlyArray<SelectOption<T>> => {
  const search = query.trim().toLowerCase();

  if (!search) return options;

  return options.filter(option =>
    [option.key, option.title, option.subTitle].some(value => value?.toLowerCase().includes(search))
  );
};

export function Typeahead<TValue = string>({
  ref,
  value,
  onChange,
  open: openProp,
  onOpenChange: onOpenChangeProp,
  disabled,
  placeholder,
  searchPlaceholder,
  options,
  search,
  filter,
  debounceTime = DEBOUNCE_TIME,
  minSearchLength,
  keyExtractor = defaultKeyExtractor as (item: SelectOption<TValue>, index: number) => string,
  renderItemIcon,
  valueFormatter,
  noDataText,
  noDataIcon,
  noDataColor,
  noDataSize,
  children,
  ...sheetProps
}: TypeaheadProps<TValue>) {
  const BottomSheetScrollComponent = useBottomSheetScrollableCreator();
  const contentContainerStyle = useSheetContainerStyles();
  const fontSize = getFontSize('$4');
  const fontSizeSubtitle = getFontSize('$3');

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ReadonlyArray<SelectOption<TValue>>>([]);
  const [isSearching, setIsSearching] = useState(false);
  const inputRef = useRef<InputRef>(null);
  const searchRef = useRef(search);
  const generation = useRef(0);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep the latest search provider without restarting an in-flight search.
  useEffect(() => {
    searchRef.current = search;
  });

  const cancelSearch = () => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    generation.current += 1;
  };

  const resetSearch = () => {
    cancelSearch();
    setQuery('');
    setResults([]);
    setIsSearching(false);
  };

  const [open, setOpen] = useControllableState({
    defaultProp: false,
    prop: openProp,
    onChange: next => {
      if (!next) {
        resetSearch();
        Keyboard.dismiss();
      }
      onOpenChangeProp?.(next);
    },
    strategy: 'most-recent-wins'
  });

  const hasSearch = !!search;
  const minLength = minSearchLength ?? (hasSearch ? 3 : 0);
  const isBelowMinLength = query.length < minLength;
  const sourceOptions = options ?? EMPTY;

  const selectedOption = isDefined(value)
    ? (sourceOptions.find(option => equals(option.value, value)) ?? {
        key: valueFormatter?.(value) ?? String(value),
        value
      })
    : undefined;

  useEffect(() => {
    if (!open) return;

    const timeout = setTimeout(() => inputRef.current?.focus(), 150);
    return () => clearTimeout(timeout);
  }, [open]);

  useEffect(
    () => () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      generation.current += 1;
    },
    []
  );

  const handleSearchQueryChange = (text: string) => {
    setQuery(text);

    if (!hasSearch) return;

    cancelSearch();

    if (text.length < minLength) {
      setResults([]);
      setIsSearching(false);
      return;
    }

    const provider = searchRef.current;
    if (!provider) return;

    const currentGeneration = generation.current;
    setIsSearching(true);

    debounceTimer.current = setTimeout(() => {
      debounceTimer.current = null;

      const resolve = (next: ReadonlyArray<SelectOption<TValue>>) => {
        if (generation.current !== currentGeneration) return;
        setResults(next);
        setIsSearching(false);
      };

      const reject = (error: unknown) => {
        if (generation.current === currentGeneration) setIsSearching(false);

        if ((error as any)?.name === CANCELLED_ERROR) return;

        Logger.error(error, 'Typeahead search failed');
      };

      try {
        const result = provider(text);
        if (isPromise(result)) result.then(resolve).catch(reject);
        else resolve(result);
      } catch (error) {
        reject(error);
      }
    }, debounceTime);
  };

  const close = () => {
    setOpen(false);
    Keyboard.dismiss();
  };

  useImperativeHandle(ref, () => ({
    open: () => setOpen(true),
    close,
    focus: () => setOpen(true),
    blur: close
  }));

  useBackHandler(
    useCallback(() => {
      if (open) {
        close();
        return true;
      }
      return false;
    }, [close, open])
  );

  const handleSelection = (option: SelectOption<TValue>) => {
    onChange?.(option.value);
    close();
  };

  const displayOptions: ReadonlyArray<SelectOption<TValue>> = (() => {
    if (search) {
      if (isBelowMinLength) {
        return sourceOptions.length ? sourceOptions : selectedOption ? [selectedOption] : EMPTY;
      }
      return results;
    }

    return filter ? filter(query, sourceOptions) : defaultFilter(query, sourceOptions);
  })();

  const showHint = isBelowMinLength && displayOptions.length === 0;

  const renderItem: ListRenderItem<SelectOption<TValue>> = ({ item, index }) => {
    const isSelected = equals(item.value, value);

    return (
      <ListItem
        cursor="pointer"
        size={'$5'}
        gap={'$3'}
        role="listitem"
        tabIndex={0}
        accessible
        aria-selected={isSelected}
        transition={[
          'medium',
          {
            y: { delay: index * 20 },
            opacity: { delay: index * 20 }
          }
        ]}
        enterStyle={{ opacity: 0, y: -20 }}
        exitStyle={{ opacity: 0, y: -20 }}
        icon={renderItemIcon?.(item)}
        iconAfter={isSelected ? <Check color={'$success'} /> : undefined}
        scaleIcon={1.5}
        onPress={() => handleSelection(item)}
        title={
          <Highlighter
            searchWords={[query]}
            textToHighlight={item.title ?? item.key}
            style={[{ fontSize }, isSelected && styles.textSelected]}
          />
        }
        subTitle={
          item.subTitle ? (
            <Highlighter
              searchWords={[query]}
              textToHighlight={item.subTitle}
              style={[
                { fontSize: fontSizeSubtitle, opacity: SUBTITLE_OPACITY },
                isSelected && styles.textSelected
              ]}
            />
          ) : undefined
        }
      />
    );
  };

  return (
    <>
      {children ? (
        <NativePressable disabled={disabled} onPress={() => setOpen(true)}>
          {children}
        </NativePressable>
      ) : null}

      <Sheet
        open={open}
        onOpenChange={setOpen}
        scrollMode="custom"
        snapPoints={['90%']}
        {...sheetProps}
      >
        <Column flex={1}>
          <Input
            ref={inputRef}
            testID="typeahead-input"
            iconAfter={isSearching ? <Spinner color={'$unistocks'} /> : <Search />}
            contentContainerProps={{ mx: '$3', mt: '$3', mb: '$2' }}
            autoCorrect={false}
            autoCapitalize="none"
            placeholder={searchPlaceholder ?? placeholder}
            value={query}
            onChangeText={handleSearchQueryChange}
            returnKeyType={'search'}
          />

          <VirtualList
            testID="typeahead-list"
            role="list"
            accessibilityRole="list"
            data={displayOptions}
            extraData={`${query}|${selectedOption?.key ?? ''}`}
            keyExtractor={keyExtractor}
            keyboardDismissMode={'interactive'}
            keyboardShouldPersistTaps={'handled'}
            showsVerticalScrollIndicator={false}
            renderItem={renderItem}
            mx={'$-3'}
            fadingEdgeLength={20}
            contentContainerStyle={contentContainerStyle as any}
            ListEmptyComponent={
              <EmptyList
                mt={'$3'}
                isLoading={isSearching}
                loadingText={''}
                noDataText={
                  showHint ? t('::TypeToShowResults') : (noDataText ?? t('::SearchResultEmpty'))
                }
                noDataIcon={showHint ? Search : (noDataIcon ?? SearchX)}
                color={noDataColor}
                size={noDataSize ?? '$6'}
              />
            }
            ItemSeparatorComponent={ItemSeparator}
            renderScrollComponent={BottomSheetScrollComponent}
          />
        </Column>
      </Sheet>
    </>
  );
}

Typeahead.displayName = 'Typeahead';

const styles = StyleSheet.create({
  textSelected: {
    fontFamily: FONT_FAMILY_SEMI_BOLD
  }
});
